"use client";

import { useId } from "react";
import { useBlogSources } from "@/components/blog/SourcesContext";

function truncateUrl(url: string, max = 48): string {
  if (url.length <= max) return url;
  return `${url.slice(0, max - 1)}…`;
}

export default function SourceRef({ n }: Readonly<{ n: number }>) {
  const sources = useBlogSources();
  const tooltipId = useId();
  const idx = n - 1;
  const source =
    idx >= 0 && idx < sources.length ? sources[idx] : undefined;
  const label = `[${n}]`;

  const tooltip = (
    <span
      id={tooltipId}
      className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-max max-w-xs -translate-x-1/2 rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      role="tooltip"
    >
      {source ? (
        <>
          <span className="block font-semibold text-gray-900">{source.title}</span>
          {source.description ? (
            <span className="mt-0.5 block text-gray-700">{source.description}</span>
          ) : null}
          {source.url ? (
            <span className="mt-1 block truncate text-gray-500">
              {truncateUrl(source.url)}
            </span>
          ) : null}
          {source.date ? (
            <span className="mt-1 block text-gray-500">{source.date}</span>
          ) : null}
        </>
      ) : (
        <span className="text-gray-500">—</span>
      )}
    </span>
  );

  const ariaCitation =
    source &&
    [source.title, source.description].filter(Boolean).join(". ");

  const inner = source?.url ? (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline decoration-gray-300 underline-offset-2 hover:decoration-primary"
      aria-label={ariaCitation ? `${label} ${ariaCitation}` : `${label} ${source.title}`}
      aria-describedby={tooltipId}
    >
      {label}
    </a>
  ) : (
    <span className="text-gray-600" aria-describedby={tooltipId}>{label}</span>
  );

  return (
    <span className="not-prose relative inline-flex align-super group mx-0.5 text-[0.75em] leading-none">
      {inner}
      {tooltip}
    </span>
  );
}
